import React, { Component } from 'react';
import { inject, observer } from 'mobx-react';

import { Field, formConsumer, InputGroup, Select, Switch } from 'core/form';
import { Callout, Flex, FlexColumn, Heading } from 'core/components';
import {
  CredentialOptionRenderer,
  CredentialValueRenderer,
  KentikAgentOptionRenderer
} from 'app/views/setup/tasks/nms/optionRenderers';
import { GROUP_ID_PREFIX, Statuses } from '@kentik/ui-shared/agents/constants';
import { CREDENTIAL_TYPES } from '@kentik/ui-shared/credentials/constants';
import { parseDuration } from '@kentik/ui-shared/nms/deviceMonitoringTemplates';
import { BsInfoCircle } from 'react-icons/bs';
import SetupSshCredentials from './SetupSshCredentials';
import {
  DEVICECONF_PLATFORM_OPTIONS,
  DEVICECONF_FETCH_INTERVAL_OPTIONS,
  DEVICECONF_FETCH_TIMEOUT_OPTIONS
} from './deviceconfConstants';

const ALLOWED_CREDENTIAL_TYPES = [CREDENTIAL_TYPES.sshkey, CREDENTIAL_TYPES.sshpw];

@formConsumer
@inject('$auth', '$devices', '$dictionary', '$credentials', '$kentikAgents', '$setup')
@observer
class CommandAccessSettings extends Component {
  state = {
    isAddingCredentialDialogOpen: false,
    credentialModel: null
  };

  constructor(props) {
    super(props);
    const { form, $kentikAgents } = this.props;

    // clear fields that have invalid values due to the referenced object getting deleted
    const credential_name = form.getValue('deviceconf_options.credential_name');
    if (credential_name && !this.credentialOptions.find(({ value }) => value === credential_name)) {
      form.setValue('deviceconf_options.credential_name', '');
    }

    const agent_id = form.getValue('deviceconf_options.agent_id');
    if (
      agent_id &&
      !agent_id.includes?.(GROUP_ID_PREFIX) &&
      !$kentikAgents.authorizedKentikAgentOptions.find(({ value }) => value === agent_id)
    ) {
      form.setValue('deviceconf_options.agent_id', '');
    }
  }

  componentDidUpdate() {}

  get credentialOptions() {
    const { $auth, $credentials } = this.props;
    const credentialOptions = $credentials.getCredentialOptionsByTypes(ALLOWED_CREDENTIAL_TYPES);
    if ($auth.hasRbacPermissions(['credential_vault::create'])) {
      credentialOptions.unshift({ value: '$NEW', label: 'New Credential', icon: 'plus', intent: 'primary' });
    }
    return credentialOptions;
  }

  get isAgentNotRunning() {
    const { $kentikAgents, form } = this.props;
    const agent_id = form.getValue('deviceconf_options.agent_id');
    return (
      agent_id &&
      !agent_id.includes?.(GROUP_ID_PREFIX) &&
      $kentikAgents.collection.find({ agent_id })?.get('status') !== Statuses.UP
    );
  }

  handleCredentialChange = (field, value) => {
    const { $credentials } = this.props;
    // open the new credential dialog
    if (value === '$NEW') {
      field.setValue('');
      this.setState({
        isAddingCredentialDialogOpen: true,
        credentialModel: $credentials.groupedCollection.forge()
      });
    }
  };

  handleConfigSyncToggle = (field, value) => {
    const { form } = this.props;
    // Reset agent_id when disabled
    if (!value && form.getValue('deviceconf_options.agent_id') !== '') {
      form.setValue('deviceconf_options.agent_id', '');
    }
  };

  handleCloseCredentialsDialog = (formValues) => {
    const { form } = this.props;

    if (formValues) {
      const { credentialName } = formValues;
      form.setValue('deviceconf_options.credential_name', credentialName);
    }

    this.setState({ isAddingCredentialDialogOpen: false, credentialModel: null });
  };

  render() {
    const { $auth, $kentikAgents, role, form } = this.props;
    const { isAddingCredentialDialogOpen, credentialModel } = this.state;
    const isEnabled = form.getValue('deviceconf_options.enabled');
    const isConfigEnabled = form.getValue('deviceconf_options.fetch_enabled');
    const fetchInterval = form.getValue('deviceconf_options.fetch_interval');
    const fetchTimeout = form.getValue('deviceconf_options.fetch_timeout');

    const fetchIntervalDuration = parseDuration(fetchInterval);
    const timeoutOptions = DEVICECONF_FETCH_TIMEOUT_OPTIONS.filter((option) => {
      if (!option.value) {
        return true;
      }

      const timeoutDuration = parseDuration(option.value);
      if (Number.isNaN(timeoutDuration)) {
        return false;
      }

      if (Number.isNaN(fetchIntervalDuration)) {
        return true;
      }

      return timeoutDuration > 0 && timeoutDuration <= fetchIntervalDuration;
    });

    // Reset fetch settings when config sync is disabled.
    if (!isConfigEnabled && form.getValue('deviceconf_options.fetch_interval') !== '') {
      form.setValue('deviceconf_options.fetch_interval', '');
    }

    if (!isConfigEnabled && form.getValue('deviceconf_options.fetch_timeout') !== '') {
      form.setValue('deviceconf_options.fetch_timeout', '');
    }

    if (isConfigEnabled && fetchTimeout) {
      const timeoutDuration = parseDuration(fetchTimeout);

      if (
        Number.isNaN(timeoutDuration) ||
        timeoutDuration <= 0 ||
        (!Number.isNaN(fetchIntervalDuration) && timeoutDuration > fetchIntervalDuration)
      ) {
        form.setValue('deviceconf_options.fetch_timeout', fetchInterval || '');
      }
    }

    const hasCredentials = !!form.getValue('deviceconf_options.credential_name');

    const ariaProps = {};
    if (role) {
      ariaProps.role = role;
    }

    return (
      <Flex flexDirection="column" gap={2} overflow="auto" p={2} {...ariaProps}>
        <FlexColumn gap={1}>
          <Heading level={5} pb="6px" mb={1} borderBottom="thin">
            SSH Command Access
          </Heading>

          <Field name="deviceconf_options.enabled" large showLabel={false} onChange={this.handleConfigSyncToggle}>
            <Switch switchLabel="Enable SSH command access to devices" />
          </Field>

          {isEnabled && (
            <Callout intent="primary" border="thin" icon={BsInfoCircle}>
              {hasCredentials
                ? `This device has been configured with credentials to enable configuration retrieval and the execution of diagnostic "show" commands.
                Kentik uses these credentials exclusively for this purpose. Write access is not required.`
                : `Please provide an account with Read-Only privileges. Kentik uses these credentials strictly for
              configuration retrieval and diagnostic "show" commands. Write access is not required.`}
            </Callout>
          )}

          {this.isAgentNotRunning && (
            <Callout intent="danger" icon="error">
              The assigned agent is not running, and will not receive updates.
            </Callout>
          )}

          <Flex gap={2}>
            <Field
              disabled={!$auth.hasRbacPermissions(['credential_vault::read']) || !isEnabled}
              m={0}
              flex={1}
              onChange={this.handleCredentialChange}
              name="deviceconf_options.credential_name"
              options={this.credentialOptions}
              large
            >
              <Select
                fill
                optionRenderer={CredentialOptionRenderer}
                valueRenderer={CredentialValueRenderer}
                showFilter
              />
            </Field>

            <Field
              m={0}
              flex={1}
              name="deviceconf_options.agent_id"
              options={$kentikAgents.kentikAgentOptionsWithKsqueegee}
              disabled={!isEnabled}
              large
            >
              <Select fill optionRenderer={KentikAgentOptionRenderer} showFilter />
            </Field>
          </Flex>

          <Flex gap={2}>
            <Field
              m={0}
              name="deviceconf_options.platform"
              flex={1}
              options={DEVICECONF_PLATFORM_OPTIONS}
              large
              disabled={!isEnabled}
            >
              <Select fill />
            </Field>

            <Field m={0} name="deviceconf_options.hostname" flex={1} disabled={!isEnabled}>
              <InputGroup fill />
            </Field>
          </Flex>
        </FlexColumn>

        {isEnabled && (
          <>
            <FlexColumn>
              <Heading level={5} pb="6px" mb={1} borderBottom="thin">
                Configuration Sync & Backup
              </Heading>
              <Field
                name="deviceconf_options.fetch_enabled"
                large
                showLabel={false}
                onChange={(field, enabled) => {
                  if (enabled && form.getValue('deviceconf_options.fetch_interval') === '') {
                    form.setValue('deviceconf_options.fetch_interval', '1h');
                  }

                  if (!enabled) {
                    form.setValue('deviceconf_options.fetch_timeout', '');
                  }
                }}
              >
                <Switch switchLabel="Enable device configuration sync and backup" />
              </Field>
              <Flex gap={2}>
                <Field
                  m={0}
                  name="deviceconf_options.fetch_interval"
                  flex={1}
                  options={DEVICECONF_FETCH_INTERVAL_OPTIONS}
                  large
                  disabled={!isConfigEnabled}
                  onChange={(field, value) => {
                    const timeout = form.getValue('deviceconf_options.fetch_timeout');
                    if (!timeout) {
                      return;
                    }

                    const timeoutDuration = parseDuration(timeout);
                    const intervalDuration = parseDuration(value);
                    if (
                      Number.isNaN(timeoutDuration) ||
                      Number.isNaN(intervalDuration) ||
                      timeoutDuration > intervalDuration
                    ) {
                      form.setValue('deviceconf_options.fetch_timeout', value || '');
                    }
                  }}
                >
                  <Select fill />
                </Field>
                <Field
                  m={0}
                  name="deviceconf_options.fetch_timeout"
                  flex={1}
                  options={timeoutOptions}
                  large
                  disabled={!isConfigEnabled}
                >
                  <Select fill />
                </Field>
              </Flex>
            </FlexColumn>
            <FlexColumn>
              <Heading level={5} pb="6px" mb={1} borderBottom="thin">
                Device Diagnostics Commands
              </Heading>
              <Field name="deviceconf_options.command_enabled" large showLabel={false}>
                <Switch switchLabel="Enable Read-Only Diagnostic Commands" />
              </Field>
            </FlexColumn>
          </>
        )}

        {credentialModel && (
          <SetupSshCredentials
            isOpen={isAddingCredentialDialogOpen}
            model={credentialModel}
            onClose={this.handleCloseCredentialsDialog}
            types={ALLOWED_CREDENTIAL_TYPES}
            title="Add SSH Credential"
          />
        )}
      </Flex>
    );
  }
}

export default CommandAccessSettings;
